<script lang="ts">
  // Forecast AI · Progress: where the ride sits in the plan, today's targets vs what was done,
  // and training status afterwards.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import ProgressRing from '../parts/ProgressRing.svelte'
  import Stars from '../parts/Stars.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'
  import XssTargets from '../parts/XssTargets.svelte'

  export let ctx: ReportContext

  $: x = ctx.data.xfai!
  $: stats = statsFor(ctx, ['stat.xss', 'stat.difficulty', 'stat.time', 'stat.distance', 'stat.xep'])
  $: dayKind = { rest: 'Rest / Recovery Day', low: 'Low Intensity Day', high: 'High Intensity Day' }[x.day_kind]
  $: showRing = ctx.show('progress') && x.day && x.days_total
  $: ringSize = { portrait: 300, square: 210, landscape: 280 }[ctx.size]
</script>

<Frame {ctx} base="radial-gradient(110% 70% at 85% 0%, #2c2f78 0%, #14163a 55%, #07081a 100%)" shade="even">
  <div class="xr-layout" class:xr-no-ring={!showRing}>
    <div class="xr-top">
      <span class="xr-eyebrow"><Icon name="sparkles" size={30} stroke={2.2} color="var(--accent)" /> Forecast AI</span>
      <Brand height={38} />
    </div>

    {#if ctx.show('plan')}
      <div class="xr-plan">
        <h2 class="xr-plan-name">{x.plan_name}</h2>
        <div class="xr-plan-meta">
          {#if x.focus}<span>{x.focus.name}{x.focus.description ? ` · ${x.focus.description}` : ''}</span>{/if}
          {#if x.phase}<span class="xr-phase">{x.phase} phase</span>{/if}
        </div>
      </div>
    {/if}

    {#if showRing}
      <div class="xr-ring"><ProgressRing value={x.day ?? 0} total={x.days_total ?? 0} size={ringSize} stroke={ctx.size === 'square' ? 22 : 26} /></div>
    {/if}
    {#if ctx.show('targets')}
      <div class="xr-today">
        <div class="xr-today-head">
          <span class="xr-today-kind">{dayKind}</span>
          <span class="xr-today-xss"><strong>{x.completed.xss}</strong> / {x.target.xss} XSS</span>
        </div>
        <XssTargets {ctx} compact={ctx.size === 'square'} />
      </div>
    {/if}

    {#if ctx.show('status') && x.status.label}
      <div class="xr-status">
        <div class="xr-status-item">
          <span class="xr-k">Training Status</span>
          <span class="xr-v">
            {#if x.status.rating !== null}<Stars value={x.status.rating} size={30} color={x.status.color ?? 'var(--accent)'} />{/if}
            {x.status.label}{x.status.form ? ` · ${x.status.form}` : ''}
          </span>
        </div>
        {#if ctx.show('recovery')}
          <div class="xr-status-item">
            <span class="xr-k">Recovery</span>
            <span class="xr-v xr-rec">
              <span class="xr-pill" style="background: #ff4d5e;">{x.recovery.low.toFixed(1)} d</span>
              <span class="xr-pill" style="background: #ffb020; color: #111;">{x.recovery.high.toFixed(1)} d</span>
            </span>
          </div>
        {/if}
      </div>
    {/if}

    <div class="xr-title"><TitleBlock {ctx} lines={1} /></div>

    <!-- No room for the chart in a square. -->
    <div class="xr-chart">
      {#if ctx.size !== 'square' && ctx.show('chart') && ctx.data.series?.power}
        <Fill minHeight={100} let:width let:height>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} {height} showMpa={false} breakthrough={ctx.data.breakthrough?.level ?? null} />
        </Fill>
      {/if}
    </div>

    <div class="xr-stats"><StatGrid {stats} columns={4} size="sm" /></div>
    <div class="xr-badges"><Badges {ctx} own="xfai" max={ctx.size === 'square' ? 2 : Infinity} /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-rows: auto auto auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'plan plan'
      'ring today'
      'status status'
      'title title'
      'chart chart'
      'stats stats'
      'badges badges';
    column-gap: 52px;
    row-gap: 28px;
  }
  .xr-layout.xr-no-ring {
    grid-template-areas: 'top top' 'plan plan' 'today today' 'status status' 'title title' 'chart chart' 'stats stats' 'badges badges';
  }
  :global(.xr-square) .xr-layout {
    column-gap: 44px;
    row-gap: 18px;
  }
  /* Landscape: the plan and where you are in it on the left; today, the ride and numbers on the right. */
  :global(.xr-landscape) .xr-layout,
  :global(.xr-landscape) .xr-layout.xr-no-ring {
    grid-template-columns: 560px minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'plan today'
      'ring title'
      'ring chart'
      'status stats'
      'badges badges';
    column-gap: 80px;
    row-gap: 28px;
  }
  .xr-top { grid-area: top; display: flex; align-items: center; justify-content: space-between; }
  .xr-plan { grid-area: plan; min-width: 0; }
  .xr-ring { grid-area: ring; display: flex; align-items: center; }
  :global(.xr-landscape) .xr-ring { justify-content: center; }
  .xr-today { grid-area: today; min-width: 0; align-self: center; }
  .xr-status { grid-area: status; }
  .xr-title { grid-area: title; min-width: 0; }
  .xr-chart { grid-area: chart; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; align-self: end; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  .xr-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.26em;
    text-transform: uppercase;
  }
  .xr-plan-name {
    font-family: var(--display);
    font-weight: 800;
    font-size: 96px;
    line-height: 0.95;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .xr-plan-meta {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-top: 12px;
    font-size: 28px;
    color: var(--muted);
  }
  .xr-phase {
    padding: 4px 16px;
    border-radius: 999px;
    border: 2px solid var(--accent);
    color: var(--accent);
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .xr-today-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 22px;
  }
  .xr-today-kind {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .xr-today-xss {
    font-family: var(--display);
    font-size: 40px;
    font-weight: 600;
    color: var(--muted);
  }
  .xr-today-xss strong {
    color: var(--accent);
    font-size: 64px;
    font-weight: 800;
  }
  .xr-status {
    display: flex;
    gap: 40px;
    padding: 24px 28px;
    border-radius: 24px;
    background: var(--panel);
    border: 2px solid var(--faint);
  }
  :global(.xr-square) .xr-status {
    padding: 18px 26px;
  }
  :global(.xr-square) .xr-plan-name {
    font-size: 76px;
  }
  :global(.xr-landscape) .xr-plan-name {
    font-size: 76px;
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  :global(.xr-landscape) .xr-plan-meta {
    flex-wrap: wrap;
    row-gap: 10px;
  }
  /* Landscape: the left column is narrow, so status stacks. */
  :global(.xr-landscape) .xr-status {
    flex-direction: column;
    gap: 18px;
    align-self: end;
  }
  :global(.xr-landscape) .xr-status-item + .xr-status-item {
    padding-left: 0;
    border-left: 0;
    padding-top: 18px;
    border-top: 2px solid var(--faint);
  }
  .xr-status-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .xr-status-item + .xr-status-item {
    padding-left: 40px;
    border-left: 2px solid var(--faint);
  }
  .xr-k {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-v {
    display: flex;
    align-items: center;
    gap: 14px;
    font-size: 28px;
    font-weight: 600;
  }
  .xr-pill {
    padding: 4px 14px;
    border-radius: 999px;
    font-weight: 700;
    color: #fff;
  }
</style>
